/**
 * @fileoverview Hook for searching external schools
 * @description Server-side search with debounce for 123k+ schools
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import type { AxiosRequestConfig } from 'axios'
import { api } from 'helpers'
import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import type { ExternalSchool, SchoolSelectItem } from './types'

const DEBOUNCE_MS = 300
const LIMIT = 10
const MIN_CHARS = 2

interface ApiSchool {
  id: string
  name: string
  city: string
  state: string
  zipcode: string
  street?: string
}

const formatSchoolItem = (school: ApiSchool): SchoolSelectItem => ({
  value: school.id,
  label: school.name,
  description: `${school.city}, ${school.state} ${school.zipcode}`,
  school: {
    id: school.id,
    name: school.name,
    city: school.city || '',
    state: school.state || '',
    zipcode: school.zipcode || '',
    street: school.street,
  },
})

export const useSchoolSearch = () => {
  const [options, setOptions] = useState<SchoolSelectItem[]>([])
  const [loading, setLoading] = useState(false)
  const [initialOptions, setInitialOptions] = useState<SchoolSelectItem[]>([])

  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * Fetch initial schools (top 10 from cache or by zipcode)
   */
  const fetchInitialSchools = useCallback(async (zipcode?: string | null) => {
    try {
      const params = zipcode
        ? { zipcode, limit: LIMIT }
        : { limit: LIMIT }

      const response = await api.get<ApiSchool[]>(
        ARC_MOSAICO_ENDPOINTS.externalSchools.list,
        { params }
      )

      const items = response.data.map(formatSchoolItem)
      setInitialOptions(items)
      setOptions(items)
    } catch (error) {
      console.error('Failed to fetch initial schools:', error)
    }
  }, [])

  /**
   * Search schools by query (debounced)
   */
  const searchSchools = useCallback(async (query: string) => {
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // If query is too short, show initial options
    if (!query || query.length < MIN_CHARS) {
      setOptions(initialOptions)
      setLoading(false)
      return
    }

    setLoading(true)

    debounceRef.current = setTimeout(async () => {
      abortControllerRef.current = new AbortController()

      try {
        const response = await api.get<ApiSchool[]>(
          ARC_MOSAICO_ENDPOINTS.externalSchools.search,
          {
            params: { q: query, limit: LIMIT },
            signal: abortControllerRef.current.signal,
          } as AxiosRequestConfig
        )

        setOptions(response.data.map(formatSchoolItem))
      } catch (error: any) {
        if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
          console.error('Search failed:', error)
          setOptions([])
        }
      } finally {
        setLoading(false)
      }
    }, DEBOUNCE_MS)
  }, [initialOptions])

  /**
   * Reset to initial options
   */
  const resetOptions = useCallback(() => {
    setOptions(initialOptions)
  }, [initialOptions])

  /**
   * Find school by ID from current options
   */
  const findSchoolById = useCallback((id: string): ExternalSchool | null => {
    const item = options.find(opt => opt.value === id)
    return item?.school || null
  }, [options])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      if (abortControllerRef.current) abortControllerRef.current.abort()
    }
  }, [])

  return {
    options,
    loading,
    searchSchools,
    fetchInitialSchools,
    resetOptions,
    findSchoolById,
  }
}
